import { useRef } from "react";

import Config from "../Config";
import type { ImageProps } from "../types/components/ImageProps";

const DEFAULT_WIDTHS = [768, 992, 1200, 1536, 1640];

export const GenerateImageLink = (src: string, width?: number) => {
	const { liveUrl, baseUrl, cloudinaryUrl } = Config;

	const widthParam = width ? (Config.CLOUDINARY_OVERRIDE ? `width=${width}` : `w_${width}`) : null;

	if (src?.includes("/app/assets")) {
		return src;
	}

	if (src?.includes("/assets/") && Config.baseUrl.includes(".local")) {
		return src;
	}

	if (src?.includes("/assets/")) {
		const link = widthParam ? `${cloudinaryUrl},${widthParam}` : `${cloudinaryUrl}`;
		return `${link}/${baseUrl}${src}`;
	}

	if (src?.includes(liveUrl)) {
		const link = widthParam ? cloudinaryUrl + "," + widthParam + "/" : cloudinaryUrl + "/";
		return link + src;
	}

	let link = src;
	link = link?.replace("react.staging.", "www.");
	link = link?.replace("staging.", "www.");
	link = link?.replace(".local", ".co.uk");
	link = widthParam ? cloudinaryUrl + "," + widthParam + "/" + link : cloudinaryUrl + "/" + link;
	return link;
};

export const GenerateSrcSet = (src: string, widths: number[] = DEFAULT_WIDTHS, maxWidth?: number): string => {
	if (!src) return "";

	// Filter widths based on maxWidth if provided
	const filteredWidths = maxWidth ? widths.filter((w) => w <= maxWidth) : widths;

	// Ensure we have at least one width
	if (filteredWidths.length === 0 && maxWidth) {
		filteredWidths.push(maxWidth);
	}

	return filteredWidths.map((width) => `${GenerateImageLink(src, width)} ${width}w`).join(", ");
};

export const getDefaultSizes = (sizes?: string): string => {
	if (sizes) return sizes;
	return "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";
};

interface ImageProps {
	src: string;
	alt: string;
	className?: string;
	width?: number;
	height?: number;
	returnOnly?: boolean;
	imported?: boolean;
	responsive?: boolean;
	sizes?: string;
	srcSetWidths?: number[];
	loading?: "lazy" | "eager";
	fetchPriority?: "high" | "low" | "auto";
	preload?: boolean;
	[key: string]: any;
}

export const Image = ({ src, alt, className, width, height, returnOnly = false, imported = false, responsive = true, sizes, srcSetWidths, loading = "lazy", fetchPriority, preload = false, ...props }: ImageProps) => {
	const imageElement = useRef<HTMLImageElement>(null);

	const ImageFallback = () => {
		if (imageElement && imageElement.current) {
			imageElement.current.src = `https://placehold.co/${width || 150}x${height || 150}?text=Image+Not+Found&font=roboto`;
		}
	};

	let imageUrl = src;
	if (!imported) {
		imageUrl = GenerateImageLink(src, width);
	}

	if (returnOnly) {
		return imageUrl;
	}

	// Generate srcSet and sizes for responsive images
	const srcSet = GenerateSrcSet(src, srcSetWidths, width ? width * 2 : undefined);
	const sizesAttr = getDefaultSizes(sizes);

	if (preload) {
		return (
			<>
				<link
					rel="preload"
					as="image"
					href={imageUrl}
				/>
				<img
					src={imageUrl}
					srcSet={srcSet}
					sizes={sizesAttr}
					alt={alt}
					className={className}
					width={width}
					height={height}
					loading={loading}
					fetchPriority={fetchPriority}
					onError={() => ImageFallback()}
					ref={imageElement}
					{...props}
					draggable="false"
					title={alt}
				/>
			</>
		);
	}

	return (
		<img
			src={imageUrl}
			srcSet={srcSet}
			sizes={sizesAttr}
			alt={alt}
			className={className}
			width={width}
			height={height}
			loading={loading}
			fetchPriority={fetchPriority}
			onError={() => ImageFallback()}
			ref={imageElement}
			{...props}
			draggable="false"
			title={alt}
		/>
	);
};
